import type { ReactElement, ReactNode } from 'react';
import { Fragment, useEffect, useState } from 'react';

import useDomElement from '@snowit/hooks/useDomElement';
import { m } from '@snowit/ui/utils/Motion';

import { AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { create } from 'zustand';

interface IBadgeProvider {
  children: ReactNode;
}

type BadgeContent = ReactElement | (() => ReactElement);
type BadgeEvent = MouseEvent;

const id = 'snowit-badge';

// scale is an attempt to use "scale" animation on the badge
// it's left at 1 because apparently javascript uses the initial
// scaled value to check how close is the object to the edge of the screen,
// resulting in it going over the edge when it's finally scaled up to its true value
// when trying to trick it and multiply the width/height by the inverse of the scale
// it works on the first transition, but not on following ones.
const scale = 1;

// delay in seconds
const delay = 0;
// timeout in milliseconds
const timeout = 100;

// timer should be at least equal to the exit time animation (+ any delay)
// this ensures that the component has time to render "out"
// before the new component enters
const timer = timeout + delay * 1000;

type BadgeStore = {
  content: ReactElement | null;
  mouse: [number, number];
  hover: boolean;
  mount: (content: BadgeContent, event: BadgeEvent, isHover?: boolean) => void;
  unmount: () => void;
};

let mountTimeout: number | undefined;

const useBadgeStore = create<BadgeStore>((set) => ({
  content: null,
  mouse: [0, 0],
  hover: false,
  mount: (content, event, isHover) => {
    const { clientX, clientY } = event;
    set({ hover: Boolean(isHover) });
    window.clearTimeout(mountTimeout);
    mountTimeout = window.setTimeout(() => {
      set({
        content: typeof content === 'function' ? content() : content,
        mouse: [clientX, clientY],
      });
    }, timer);
  },
  unmount: () => {
    window.clearTimeout(mountTimeout);
    set({ content: null });
  },
}));

const BadgeProvider = (props: IBadgeProvider) => {
  const { children } = props;
  const badgeRoot = useDomElement({ prefix: 'badges', id: 'badge-root' });
  const content = useBadgeStore((state) => state.content);
  const mouse = useBadgeStore((state) => state.mouse);
  const hover = useBadgeStore((state) => state.hover);
  const unmount = useBadgeStore((state) => state.unmount);
  const [position, setPosition] = useState<[number, number]>([0, 0]);

  const handlePosition = (w: number, h: number, mx: number, my: number, iw: number, ih: number) => {
    const W = w * (1 / scale);
    const H = h * (1 / scale);
    const horizontal = mx + W < iw;
    const vertical = my + H < ih;
    const left = mx;
    const right = mx - W;
    const bottom = my;
    const top = my - H;

    setPosition([horizontal ? left : right, vertical ? bottom : top]);
  };

  useEffect(() => {
    const el = document.getElementById(id);
    if (!el) return () => {};
    const { width, height } = el.getBoundingClientRect();
    const { innerWidth: w, innerHeight: h } = window;
    handlePosition(width, height, mouse[0], mouse[1], w, h);
    const handleClick = (event: MouseEvent) => {
      for (const target of event.composedPath()) {
        if ((target as HTMLElement).id === id) return;
      }
      unmount();
    };
    // capture phase: the outside-click unmount must run *before* React's onClick
    // schedules the next badge, otherwise unmount cancels the pending mount timeout
    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('click', handleClick, true);
    };
  }, [content, mouse, unmount]);

  // const noop = (e: any) => e.stopPropagation();

  const badgeContainer = (
    <AnimatePresence>
      {content ? (
        <m.div
          initial={{ opacity: 0, scale: scale, x: 10 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: scale, x: -10 }}
          transition={{
            delay: delay,
            type: 'spring',
            stiffness: 500,
            damping: 40,
          }}
          style={{
            position: 'fixed',
            left: position[0],
            top: position[1],
            zIndex: 99999,
            ...(hover && { pointerEvents: 'none' }),
          }}
        >
          <div id={id}>{content}</div>
        </m.div>
      ) : null}
    </AnimatePresence>
  );

  return (
    <Fragment>
      {children}
      {badgeRoot && (createPortal(badgeContainer, badgeRoot) as ReactElement)}
    </Fragment>
  );
};

export const useBadge = () => {
  const mount = useBadgeStore((state) => state.mount);
  const unmount = useBadgeStore((state) => state.unmount);
  return { mount, unmount };
};

export default BadgeProvider;
